import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { ApiError } from '@/api/client'
import { QueryState } from '@/components/QueryState'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { explainInvoicePrice } from '@/features/assistant/api'
import { ExplainPrice } from '@/features/assistant/AssistantPanel'
import { useAuth } from '@/features/auth/store'
import { supplyLabel, useRegime } from '@/features/company/regime'
import { RecordPaymentDialog } from '@/features/invoices/RecordPaymentDialog'
import { StatusBadge } from '@/features/invoices/StatusBadge'
import {
  METHOD_LABELS,
  daysPastDue,
  deleteInvoice,
  fetchInvoicePdf,
  formatMoney,
  getInvoice,
  issueInvoice,
  voidInvoice,
  voidPayment,
  type Invoice,
  type Payment,
} from '@/features/invoices/api'
import { formatPercent, formatSigned } from '@/lib/money'
import { toast } from '@/lib/toast'

function Detail({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm text-slate-900 dark:text-slate-100">{children}</dd>
    </div>
  )
}

/** The bill-to block as it was frozen when the invoice was raised, not as the customer is now. */
function BillTo({
  data,
  registrationLabel,
}: {
  data: Invoice['customer_data']
  registrationLabel: string
}) {
  const line = (key: string) => (data[key] == null ? null : String(data[key]))
  const town = [line('city'), line('state'), line('postal_code')].filter(Boolean).join(' ')
  const lines = [
    line('name'),
    line('address_line1'),
    line('address_line2'),
    town || null,
    line('country'),
  ].filter(Boolean)

  return (
    <div className="text-sm text-slate-900 dark:text-slate-100">
      {lines.map((text) => (
        <div key={text}>{text}</div>
      ))}
      {line('tax_id') && (
        <div className="mt-1 text-slate-500 dark:text-slate-400">
          {registrationLabel}: {line('tax_id')}
        </div>
      )}
    </div>
  )
}

function OverdueNotice({ invoice, currency }: { invoice: Invoice; currency: string | null }) {
  const late = daysPastDue(invoice.due_date)
  if (invoice.status === 'paid' || invoice.status === 'void' || invoice.status === 'draft') {
    return null
  }
  if (late <= 0) return null

  return (
    <p className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
      {formatMoney(invoice.balance_due, currency)} has been outstanding for {late} day
      {late === 1 ? '' : 's'} past the due date.
    </p>
  )
}

export function InvoiceDetail() {
  const { invoiceId = '' } = useParams()
  const [paying, setPaying] = useState(false)
  const [voiding, setVoiding] = useState(false)
  const [voidingPayment, setVoidingPayment] = useState<Payment | null>(null)
  const [reason, setReason] = useState('')
  const can = useAuth((s) => s.can)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const {
    data: invoice,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['invoice', invoiceId],
    queryFn: () => getInvoice(invoiceId),
    retry: false,
  })

  /**
   * The regime this document was raised under, not the one in force today.
   *
   * A client who switched jurisdiction in October must still see September's invoices the
   * way they were printed: the regime was frozen onto `company_data` when the invoice was
   * raised, and everything below reads its labels from there. `useRegime` falls back to
   * the active one while the invoice itself is still loading, which is a frame nobody sees.
   */
  const seller = invoice?.company_data
  const regime = useRegime(typeof seller?.tax_regime === 'string' ? seller.tax_regime : null)
  // The document's own currency, for the same reason. `null` means "however this
  // deployment bills", which is right for an invoice raised before the column existed.
  const currency = typeof seller?.currency === 'string' ? seller.currency : null
  const money = (amount: string | number | null | undefined) =>
    formatMoney(amount, currency, regime.locale)
  const percent = (rate: string | number | null | undefined) => formatPercent(rate, regime.locale)

  /**
   * One mutation for every action that changes the invoice.
   *
   * Same shape as the order screen: the calls differ only in what they run and what to say
   * afterwards, and a refusal is a business answer -- already issued, has payments against
   * it -- so the server's message is shown as it stands.
   */
  const act = useMutation({
    mutationFn: ({ run }: { run: () => Promise<unknown>; message: string }) => run(),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      void queryClient.invalidateQueries({ queryKey: ['invoices'] })
      void queryClient.invalidateQueries({ queryKey: ['invoice-aging'] })
      setVoiding(false)
      setVoidingPayment(null)
      setReason('')
      toast.success(variables.message)
    },
    onError: (error: unknown) => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', invoiceId] })
      toast.error(
        error instanceof ApiError
          ? error.message
          : 'Could not reach the server. Nothing was changed.',
      )
    },
  })

  /**
   * The PDF is fetched rather than linked because the route needs the bearer token, then
   * opened from a blob URL. The URL is revoked on the next tick -- the browser has already
   * taken what it needs, and an object URL that is never revoked holds the whole document
   * in memory for as long as the tab lives.
   */
  const download = useMutation({
    mutationFn: async (invoice: Invoice) => {
      const blob = await fetchInvoicePdf(invoice.id)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${invoice.invoice_number}.pdf`
      document.body.appendChild(link)
      link.click()
      link.remove()
      setTimeout(() => URL.revokeObjectURL(url), 0)
    },
    onError: (error: unknown) =>
      toast.error(
        error instanceof ApiError ? error.message : 'Could not download the invoice PDF.',
      ),
  })

  if (isLoading || error || !invoice) {
    return (
      <QueryState
        isLoading={isLoading}
        error={error}
        isEmpty={!invoice}
        emptyMessage="That invoice does not exist, or is not yours to see."
      >
        {null}
      </QueryState>
    )
  }

  const canEdit = can('invoices', 'update')
  const canVoid = can('invoices', 'delete')

  // Which tax columns this document has, and what they are called, comes from the regime
  // it was raised under -- never from the field names, which are shape and not country. A
  // pair of labels is two authorities on one supply (India's CGST + SGST); one label is a
  // single authority (India's IGST, and every US sale); none is a supply nothing could
  // classify, where the undivided tax is all there is to show.
  const [primaryLabel, secondaryLabel, unifiedLabel] = regime.component_labels[
    invoice.supply_type
  ] ?? [null, null, null]
  const split = Boolean(primaryLabel && secondaryLabel)
  const unified = !split && Boolean(unifiedLabel)

  // Summed from the lines rather than sent as a header total: each line's tax was rounded
  // once and split there, and adding the pieces back up is what proves the document.
  const sum = (of: (item: Invoice['items'][number]) => string) =>
    invoice.items.reduce((total, item) => total + Number(of(item)), 0)
  const primaryTax = sum((item) => item.primary_tax_amount)
  const secondaryTax = sum((item) => item.secondary_tax_amount)
  const unifiedTax = sum((item) => item.unified_tax_amount)
  const isDraft = invoice.status === 'draft'
  const collectable = ['issued', 'partially_paid', 'overdue'].includes(invoice.status)

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link
            to="/invoices"
            className="text-sm text-slate-500 hover:underline dark:text-slate-400"
          >
            ← All invoices
          </Link>
          <h1 className="mt-1 flex items-center gap-3 text-xl font-semibold tracking-tight">
            {invoice.invoice_number}
            <StatusBadge status={invoice.status} />
          </h1>
        </div>

        <div className="flex flex-wrap gap-2">
          {isDraft && canEdit && (
            <Button
              loading={act.isPending}
              onClick={() =>
                act.mutate({
                  run: () => issueInvoice(invoice.id),
                  message: 'Issued. The customer has been emailed a copy.',
                })
              }
            >
              Issue invoice
            </Button>
          )}
          {collectable && canEdit && (
            <Button onClick={() => setPaying(true)}>Record payment</Button>
          )}
          {!isDraft && (
            <Button
              variant="secondary"
              loading={download.isPending}
              onClick={() => download.mutate(invoice)}
            >
              Download PDF
            </Button>
          )}
          {isDraft && canVoid && (
            <Button
              variant="danger"
              loading={act.isPending}
              onClick={() =>
                act.mutate({
                  run: async () => {
                    await deleteInvoice(invoice.id)
                    navigate('/invoices')
                  },
                  message: 'Draft deleted.',
                })
              }
            >
              Delete draft
            </Button>
          )}
          {!isDraft && invoice.status !== 'void' && canVoid && (
            <Button variant="danger" onClick={() => setVoiding(true)}>
              Void invoice
            </Button>
          )}
        </div>
      </header>

      <OverdueNotice invoice={invoice} currency={currency} />

      {invoice.anomalies && invoice.anomalies.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        >
          <p className="flex items-center gap-1.5 font-medium">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Check this invoice
          </p>
          <ul className="mt-1 list-disc pl-6">
            {invoice.anomalies.map((flag, index) => (
              <li key={`${flag.kind}-${flag.item_id ?? index}`}>{flag.message}</li>
            ))}
          </ul>
        </div>
      )}

      {invoice.status === 'void' && (
        <p className="rounded-md bg-slate-100 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
          Voided {invoice.voided_at ? new Date(invoice.voided_at).toLocaleString() : ''}
          {invoice.void_reason ? `: ${invoice.void_reason}` : '.'} The quantities it billed are
          available to invoice again.
        </p>
      )}

      <Card>
        <CardHeader title="Invoice" />
        <CardBody>
          <dl className="grid grid-cols-2 gap-4 tablet:grid-cols-4">
            <Detail label="Raised">{new Date(invoice.invoice_date).toLocaleDateString()}</Detail>
            <Detail label="Due">
              {new Date(`${invoice.due_date}T00:00:00`).toLocaleDateString()}
            </Detail>
            <Detail label="Issued">
              {invoice.issued_at ? new Date(invoice.issued_at).toLocaleString() : '—'}
            </Detail>
            <Detail label="Emailed">
              {invoice.sent_at ? new Date(invoice.sent_at).toLocaleString() : '—'}
            </Detail>
            <Detail label={regime.place_label}>
              {invoice.place_of_supply
                ? `${invoice.place_of_supply}${
                    invoice.place_of_supply_code ? ` (${invoice.place_of_supply_code})` : ''
                  }`
                : '—'}
            </Detail>
            <Detail label="Tax applied">{supplyLabel(regime, invoice.supply_type)}</Detail>
          </dl>

          <div className="mt-5 grid gap-5 tablet:grid-cols-2">
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                Bill to
              </h3>
              <div className="mt-1">
                <BillTo
                  data={invoice.customer_data}
                  registrationLabel={regime.customer_registration_label}
                />
              </div>
              <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
                Copied when the invoice was raised, so a later change of address cannot rewrite this
                document.
              </p>
            </div>
            <div className="space-y-3">
              {invoice.order_id && (
                <Detail label="From order">
                  <Link
                    to={`/orders/${invoice.order_id}`}
                    className="text-brand-600 hover:underline dark:text-brand-400"
                  >
                    View the order
                  </Link>
                </Detail>
              )}
              {invoice.terms && <Detail label="Terms">{invoice.terms}</Detail>}
              {invoice.notes && (
                <Detail label="Notes">
                  <span className="whitespace-pre-line">{invoice.notes}</span>
                </Detail>
              )}
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Lines" description="Prices as they were agreed on the order." />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
              <tr>
                <th className="px-5 py-3">Product</th>
                <th className="px-5 py-3">{regime.product_code_label}</th>
                <th className="px-5 py-3 text-right">Qty</th>
                <th className="px-5 py-3 text-right">Unit price</th>
                <th className="px-5 py-3 text-right">Discount</th>
                <th className="px-5 py-3 text-right">Rate</th>
                {/* Only the pair that applies: two empty columns on every line teach
                    nobody anything and make the total harder to follow. */}
                {split && (
                  <>
                    <th className="px-5 py-3 text-right">{primaryLabel}</th>
                    <th className="px-5 py-3 text-right">{secondaryLabel}</th>
                  </>
                )}
                {unified && <th className="px-5 py-3 text-right">{unifiedLabel}</th>}
                {!split && !unified && <th className="px-5 py-3 text-right">Tax</th>}
                <th className="px-5 py-3 text-right">Line total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
              {invoice.items.map((item) => (
                <tr key={item.id}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-1.5 font-medium">
                      {item.product_data.name ?? 'Product'}
                      {item.product_data.price_anomaly && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-normal text-amber-800 dark:bg-amber-500/10 dark:text-amber-400"
                          title={
                            item.product_data.price_anomaly_reason ??
                            'Price outside the expected range'
                          }
                        >
                          <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                          Price flagged
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">
                      {item.product_data.code}
                    </div>
                    {item.product_data.price_anomaly && (
                      <ExplainPrice ask={() => explainInvoicePrice(invoice.id, item.id)} />
                    )}
                  </td>
                  <td className="px-5 py-3 tabular-nums text-slate-500 dark:text-slate-400">
                    {item.tax_code ?? '—'}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">{item.quantity}</td>
                  <td className="px-5 py-3 text-right tabular-nums">{money(item.unit_price)}</td>
                  <td className="px-5 py-3 text-right tabular-nums">
                    {money(item.discount_amount)}
                  </td>
                  <td className="px-5 py-3 text-right tabular-nums">{percent(item.tax_rate)}</td>
                  {split && (
                    <>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {money(item.primary_tax_amount)}
                      </td>
                      <td className="px-5 py-3 text-right tabular-nums">
                        {money(item.secondary_tax_amount)}
                      </td>
                    </>
                  )}
                  {unified && (
                    <td className="px-5 py-3 text-right tabular-nums">
                      {money(item.unified_tax_amount)}
                    </td>
                  )}
                  {!split && !unified && (
                    <td className="px-5 py-3 text-right tabular-nums">{money(item.tax_amount)}</td>
                  )}
                  <td className="px-5 py-3 text-right font-medium tabular-nums">
                    {money(item.line_total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end border-t border-slate-200 px-5 py-4 dark:border-slate-800">
          <dl className="w-64 space-y-1 text-sm">
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Subtotal</dt>
              <dd className="tabular-nums">{money(invoice.subtotal)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Discount</dt>
              <dd className="tabular-nums">−{money(invoice.discount_amount)}</dd>
            </div>
            {split ? (
              <>
                <div className="flex justify-between">
                  <dt className="text-slate-500 dark:text-slate-400">{primaryLabel}</dt>
                  <dd className="tabular-nums">{money(String(primaryTax))}</dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-slate-500 dark:text-slate-400">{secondaryLabel}</dt>
                  <dd className="tabular-nums">{money(String(secondaryTax))}</dd>
                </div>
              </>
            ) : unified ? (
              <div className="flex justify-between">
                <dt className="text-slate-500 dark:text-slate-400">{unifiedLabel}</dt>
                <dd className="tabular-nums">{money(String(unifiedTax))}</dd>
              </div>
            ) : (
              <div className="flex justify-between">
                <dt className="text-slate-500 dark:text-slate-400">Tax</dt>
                <dd className="tabular-nums">{money(invoice.tax_amount)}</dd>
              </div>
            )}
            {Number(invoice.shipping_amount) > 0 && (
              <div className="flex justify-between">
                <dt className="text-slate-500 dark:text-slate-400">Delivery</dt>
                <dd className="tabular-nums">{money(invoice.shipping_amount)}</dd>
              </div>
            )}
            {Number(invoice.round_off) !== 0 && (
              <div className="flex justify-between">
                <dt className="text-slate-500 dark:text-slate-400">Round off</dt>
                <dd className="tabular-nums">
                  {formatSigned(invoice.round_off, currency, regime.locale)}
                </dd>
              </div>
            )}
            <div className="flex justify-between border-t border-slate-200 pt-1 font-semibold dark:border-slate-800">
              <dt>Total</dt>
              <dd className="tabular-nums">{money(invoice.total)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-500 dark:text-slate-400">Paid</dt>
              <dd className="tabular-nums">{money(invoice.amount_paid)}</dd>
            </div>
            <div className="flex justify-between font-semibold">
              <dt>Outstanding</dt>
              <dd className="tabular-nums">{money(invoice.balance_due)}</dd>
            </div>
          </dl>
        </div>
      </Card>

      {invoice.tax_breakdown.length > 0 && (
        <Card>
          <CardHeader
            title="Tax breakdown"
            description="One band per classification and rate, which is how a return is filed. The rule that priced each is named, so the rate can still be explained after the rules change."
          />
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3">Rule</th>
                  <th className="px-5 py-3">{regime.product_code_label}</th>
                  <th className="px-5 py-3 text-right">Rate</th>
                  <th className="px-5 py-3 text-right">Taxable</th>
                  <th className="px-5 py-3 text-right">Tax</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {invoice.tax_breakdown.map((band, index) => (
                  <tr
                    key={`${band.tax_rule_id ?? 'none'}-${band.tax_code ?? 'none'}-${band.rate}-${index}`}
                  >
                    <td className="px-5 py-3">
                      {band.tax_rule_name ?? 'Standard'}
                      {band.valid_from && (
                        <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                          in force from{' '}
                          {new Date(`${band.valid_from}T00:00:00`).toLocaleDateString()}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3 tabular-nums">{band.tax_code ?? '—'}</td>
                    <td className="px-5 py-3 text-right tabular-nums">
                      {Number(band.rate).toFixed(2)}%
                    </td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(band.taxable)}</td>
                    <td className="px-5 py-3 text-right tabular-nums">{money(band.tax)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card>
        <CardHeader
          title="Payments"
          description="Recorded, never edited. A payment that should not have been taken is voided and stays on the record."
        />
        {invoice.payments.length === 0 ? (
          <CardBody>
            <p className="text-sm text-slate-500 dark:text-slate-400">Nothing received yet.</p>
          </CardBody>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800 dark:text-slate-400">
                <tr>
                  <th className="px-5 py-3">Received</th>
                  <th className="px-5 py-3">Method</th>
                  <th className="px-5 py-3">Reference</th>
                  <th className="px-5 py-3 text-right">Amount</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                {invoice.payments.map((payment) => (
                  <tr key={payment.id}>
                    <td className="px-5 py-3">
                      {new Date(payment.received_at).toLocaleString()}
                      {payment.status !== 'completed' && (
                        <span className="ml-2 text-xs uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          {payment.status}
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      {METHOD_LABELS[payment.method] ?? payment.method}
                      {payment.gateway_txn_id && (
                        <div className="text-xs text-slate-500 dark:text-slate-400">
                          {payment.gateway_txn_id}
                        </div>
                      )}
                    </td>
                    <td className="px-5 py-3 text-slate-500 dark:text-slate-400">
                      {payment.reference ?? '—'}
                      {payment.void_reason && (
                        <div className="text-xs text-danger">Voided: {payment.void_reason}</div>
                      )}
                    </td>
                    <td
                      className={
                        payment.status === 'voided'
                          ? 'px-5 py-3 text-right tabular-nums text-slate-400 line-through'
                          : 'px-5 py-3 text-right font-medium tabular-nums'
                      }
                    >
                      {money(payment.amount)}
                    </td>
                    <td className="px-5 py-3 text-right">
                      {payment.status !== 'voided' && canVoid && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setVoidingPayment(payment)}
                        >
                          Void
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <RecordPaymentDialog invoice={invoice} open={paying} onClose={() => setPaying(false)} />

      <Modal
        open={voiding}
        onClose={() => setVoiding(false)}
        title="Void this invoice?"
        description="The document stays on the record and stops counting towards what the customer owes. What it billed can be invoiced again."
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoiding(false)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={act.isPending}
              disabled={reason.trim().length < 3}
              onClick={() =>
                act.mutate({
                  run: () => voidInvoice(invoice.id, reason.trim()),
                  message: 'Invoice voided.',
                })
              }
            >
              Void invoice
            </Button>
          </>
        }
      >
        <label
          className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          htmlFor="void-reason"
        >
          Reason
        </label>
        <Input
          id="void-reason"
          className="mt-1.5"
          value={reason}
          maxLength={500}
          placeholder="Billed the wrong customer"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>

      <Modal
        open={voidingPayment !== null}
        onClose={() => setVoidingPayment(null)}
        title="Void this payment?"
        description={
          voidingPayment?.status === 'unapplied'
            ? 'This money never counted against the balance. Void it once it has been refunded to the customer; the payment stays on the record as voided.'
            : 'The payment stays on the record as voided and the balance goes back up. Use this for a bounced cheque or money recorded against the wrong invoice.'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setVoidingPayment(null)}>
              Keep it
            </Button>
            <Button
              variant="danger"
              loading={act.isPending}
              disabled={reason.trim().length < 3}
              onClick={() =>
                voidingPayment &&
                act.mutate({
                  run: () => voidPayment(voidingPayment.id, reason.trim()),
                  message:
                    voidingPayment.status === 'unapplied'
                      ? 'Payment voided.'
                      : 'Payment voided. The balance has gone back up.',
                })
              }
            >
              Void payment
            </Button>
          </>
        }
      >
        <label
          className="block text-sm font-medium text-slate-700 dark:text-slate-300"
          htmlFor="void-payment-reason"
        >
          Reason
        </label>
        <Input
          id="void-payment-reason"
          className="mt-1.5"
          value={reason}
          maxLength={500}
          placeholder="Cheque bounced"
          onChange={(event) => setReason(event.target.value)}
        />
      </Modal>
    </div>
  )
}
